import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

const getHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

// ─── Title Debts ────────────────────────────────────────────────────────────

export const listTitleDebts = async (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getTitleDebtDetail = async (id: number) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${id}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getTitleDebtMovements = async (id: number) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${id}/movements`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Payments (Baixas) ──────────────────────────────────────────────────────

export interface RegisterPaymentPayload {
    amount: number;
    payment_type: string;
    payment_method?: string;
    settlement_type: string;
    bank_account?: string;
    payment_source?: string;
    is_subrogation?: boolean;
    pending_guarantor_refund?: boolean;
    notes?: string;
    debt_confession_contract?: string;
    asset_dation_list?: any[];
}

export const registerPayment = async (titleDebtId: number, payload: RegisterPaymentPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtId}/payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const message = errorBody?.message || `Erro HTTP ${res.status}`;
        throw new Error(message);
    }
    return res.json();
};

// TODO: Endpoint batch-payments (AF-2729 Fase 3)
export interface BatchPaymentItem {
    title_debt_id: number;
    amount: number;
    payment_method: string;
    settlement_type: string;
    bank_account?: string;
    payment_source?: string;
    notes?: string;
}

export const registerBatchPayments = async (payments: BatchPaymentItem[]) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/batch-payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ payments }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Reversals ──────────────────────────────────────────────────────────────

export interface ReversalPayload {
    reasons: string[];
    notes?: string;
}

export const registerReversal = async (titleDebtId: number, payload: ReversalPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtId}/reversals`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const message = errorBody?.message || `Erro HTTP ${res.status}`;
        throw new Error(message);
    }
    return res.json();
};

// ─── Invoices ───────────────────────────────────────────────────────────────

export const listInvoices = async (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Simulations (TODO: settlement-simulation-api) ──────────────────────────

export interface PaymentSimulationPayload {
    contract_operation_id: string;
    amount: number;
    payment_date: string;
    payment_source?: string;
}

export const simulatePayment = async (payload: PaymentSimulationPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/simulations/payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getMaturityBuckets = async (contractOperationId?: string) => {
    const headers = await getHeaders();
    const qs = contractOperationId ? `?contract_operation_id=${contractOperationId}` : '';
    const res = await fetch(`${API_URL}/maturities/buckets${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getMaturityProjections = async (contractOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/maturities/projections?contract_operation_id=${contractOperationId}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const simulateEarlyPayment = async (titleDebtId: number, paymentDate: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/simulations/early-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title_debt_id: titleDebtId, payment_date: paymentDate }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getFinancialMetrics = async (contractOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/financials/${contractOperationId}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};
